// Hero atmosphere — custom GLSL.
// Mounts atmosphere.frag on the existing #atmosBase host. Pointer position
// drives the sun term. Scroll velocity briefly churns the cloud structure.
// Pauses when hero scrolls out of view.

import { ShaderCanvas } from './ShaderCanvas';
import atmosphereSrc from './atmosphere.frag';

export function initHeroAtmosphere(): (() => void) | null {
  const host = document.getElementById('atmosBase');
  const wrapper = document.getElementById('heroAtmos');
  const heroSection = document.querySelector<HTMLElement>('.s--hero');
  if (!host || !wrapper || !heroSection) return null;

  // Ensure the host fills its parent — ShaderCanvas appends a positioned canvas.
  // Override the multiply blend from the original .atmos-layer rule: my shader
  // outputs sky-friendly colors at low opacity that look right with normal blend.
  host.style.position = 'absolute';
  host.style.inset = '0';
  host.style.mixBlendMode = 'normal';
  wrapper.style.setProperty('--atmos-opacity', '0.6');
  wrapper.style.setProperty('--atmos-opacity-dark', '0.55');

  const canvas = new ShaderCanvas(host, atmosphereSrc, {
    pixelRatioCap: 2,
    uniforms: {
      u_scrollVelocity: 0,
      u_dark: document.documentElement.dataset.theme === 'dark' ? 1 : 0,
    },
  });

  // Mark the wrapper ready so its CSS opacity transition kicks in.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => wrapper.classList.add('ready'));
  });

  canvas.start();

  // Visibility gate — pause when hero leaves viewport.
  let heroVisible = true;
  const visIo = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        heroVisible = entry.isIntersecting;
        if (entry.isIntersecting) canvas.start();
        else canvas.stop();
      });
    },
    { threshold: 0 }
  );
  visIo.observe(heroSection);

  // Pointer + scroll-velocity wiring.
  let targetX = 0;
  let targetY = 0;
  let mouseX = 0;
  let mouseY = 0;
  let scrollVelocity = 0;
  let scrollTargetVelocity = 0;
  let lastScrollY = window.scrollY;

  window.addEventListener(
    'scroll',
    () => {
      const dy = window.scrollY - lastScrollY;
      lastScrollY = window.scrollY;
      const vel = Math.min(Math.abs(dy) / 40, 1);
      if (vel > scrollTargetVelocity) scrollTargetVelocity = vel;
    },
    { passive: true }
  );

  heroSection.addEventListener(
    'pointermove',
    (e) => {
      const rect = heroSection.getBoundingClientRect();
      targetX = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      targetY = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    },
    { passive: true }
  );
  heroSection.addEventListener(
    'pointerleave',
    () => {
      targetX = 0;
      targetY = 0;
    },
    { passive: true }
  );
  heroSection.addEventListener(
    'pointerdown',
    () => {
      // Click ripple → temporary scroll-velocity boost so the sky churns.
      scrollTargetVelocity = Math.max(scrollTargetVelocity, 1.0);
    },
    { passive: true }
  );

  // Theme change → flip u_dark.
  const themeObs = new MutationObserver(() => {
    canvas.setUniforms({
      u_dark: document.documentElement.dataset.theme === 'dark' ? 1 : 0,
    });
  });
  themeObs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

  // Per-frame uniform update — runs alongside the canvas's own rAF.
  function tick() {
    if (heroVisible) {
      mouseX += (targetX - mouseX) * 0.06;
      mouseY += (targetY - mouseY) * 0.06;
      scrollTargetVelocity *= 0.88;
      scrollVelocity += (scrollTargetVelocity - scrollVelocity) * 0.14;
      canvas.setMouse(mouseX, -mouseY); // flip Y to match GL coords (origin bottom)
      canvas.setUniforms({ u_scrollVelocity: scrollVelocity });
    }
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);

  return () => {
    visIo.disconnect();
    themeObs.disconnect();
    canvas.dispose();
  };
}
